import React, { useEffect, useState } from 'react';
import { relatoriosAPI } from '../lib/api';

const formatCurrency = (v) => 'R$ ' + (Number(v) || 0).toFixed(2).replace('.', ',');
const formatDate = (d) => { try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return '-'; } };

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  trialing: 'bg-blue-100 text-blue-700',
  suspended: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

export default function RelatorioIndicacoes() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await relatoriosAPI.indicacoes();
      setData(res.data);
    } catch {
      setData({ totais: {}, por_indicador: [], sem_indicacao: 0 });
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const lines = [
      'Indicador;Contato;Total Tenants;Ativos;Trial;Suspensos;Valor Mensal Total;Valor Anual Total',
    ];
    data.por_indicador.forEach((r) => {
      lines.push([
        r.indicado_por_nome, r.indicado_por_contato,
        r.total_tenants, r.ativos, r.trial, r.suspensos,
        r.valor_mensal_total.toFixed(2).replace('.', ','),
        r.valor_anual_total.toFixed(2).replace('.', ','),
      ].join(';'));
    });
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `indicacoes_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Carregando relatório...</div>;
  }

  const filtered = (data?.por_indicador || []).filter((r) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (r.indicado_por_nome || '').toLowerCase().includes(s) ||
           (r.indicado_por_contato || '').toLowerCase().includes(s);
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="relatorio-indicacoes-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatório de Indicações</h1>
          <p className="text-sm text-gray-500">Tenants agrupados por quem os indicou.</p>
        </div>
        <button
          onClick={exportCsv}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
          data-testid="export-csv-btn"
        >
          Exportar CSV
        </button>
      </div>

      {/* Totais */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Indicadores', value: data?.totais?.total_indicadores || 0, color: 'bg-purple-50 text-purple-700 border-purple-200' },
          { label: 'Tenants indicados', value: data?.totais?.total_tenants_indicados || 0, color: 'bg-blue-50 text-blue-700 border-blue-200' },
          { label: 'Receita mensal', value: formatCurrency(data?.totais?.valor_mensal_total), color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
          { label: 'Sem indicação', value: data?.sem_indicacao || 0, color: 'bg-gray-50 text-gray-700 border-gray-200' },
          { label: 'Ativos / Trial / Susp', value: `${data?.totais?.ativos || 0} / ${data?.totais?.trial || 0} / ${data?.totais?.suspensos || 0}`, color: 'bg-amber-50 text-amber-700 border-amber-200' },
        ].map((c, i) => (
          <div key={i} className={`rounded-lg border p-3 ${c.color}`}>
            <p className="text-xs font-medium uppercase tracking-wider">{c.label}</p>
            <p className="text-xl font-bold mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Busca */}
      <input
        type="search"
        placeholder="Buscar indicador..."
        className="w-full px-3 py-2 border rounded-lg"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="search-indicador"
      />

      {/* Lista */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            Nenhum tenant com indicação encontrada.
          </div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
              <tr>
                <th className="text-left px-4 py-3">Indicador</th>
                <th className="text-left px-4 py-3">Contato</th>
                <th className="text-right px-4 py-3">Tenants</th>
                <th className="text-right px-4 py-3">Ativos</th>
                <th className="text-right px-4 py-3">Trial</th>
                <th className="text-right px-4 py-3">Susp.</th>
                <th className="text-right px-4 py-3">Receita mensal</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((r, idx) => {
                const isOpen = expanded[r.indicado_por_nome];
                return (
                  <React.Fragment key={idx}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpanded((p) => ({ ...p, [r.indicado_por_nome]: !isOpen }))}
                      data-testid={`indicador-row-${idx}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{r.indicado_por_nome}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.indicado_por_contato || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.total_tenants}</td>
                      <td className="px-4 py-3 text-right text-emerald-700">{r.ativos}</td>
                      <td className="px-4 py-3 text-right text-blue-700">{r.trial}</td>
                      <td className="px-4 py-3 text-right text-red-700">{r.suspensos}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-700">
                        {formatCurrency(r.valor_mensal_total)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-gray-400 text-sm">{isOpen ? '▾' : '▸'}</span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50/60">
                        <td colSpan="8" className="px-4 py-3">
                          <div className="space-y-1.5">
                            {r.tenants.map((t) => (
                              <div key={t.id} className="flex items-center gap-2 text-sm bg-white border rounded p-2">
                                <span className="font-medium flex-1">{t.nome}</span>
                                <span className="text-xs text-gray-500">{t.dominio || t.slug}</span>
                                <span className="font-mono text-xs">{formatCurrency(t.valor_mensal)}</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[t.subscription_status] || 'bg-gray-100'}`}>
                                  {t.subscription_status || '-'}
                                </span>
                                <span className="text-xs text-gray-500">{formatDate(t.created_at)}</span>
                                {t.observacao && (
                                  <span className="text-xs text-gray-400 italic" title={t.observacao}>
                                    {t.observacao.length > 30 ? t.observacao.slice(0, 30) + '...' : t.observacao}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
